import {
  AudioLines,
  ArrowLeft,
  Bot,
  Check,
  ChevronRight,
  FilePenLine,
  FileText,
  History,
  Image as ImageIcon,
  Link2,
  LoaderCircle,
  MessageCircle,
  Mic,
  PanelRightClose,
  PanelRightOpen,
  Plus,
  Paperclip,
  Search,
  Send,
  Sparkles,
  Square,
  Unlink,
  X,
} from "lucide-react";
import {
  Component,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { normalizeCitationReferences } from "@tribunalea/shared";
import { createJustinianoClient } from "./client.js";
import { CitationCard } from "./CitationCard.jsx";
import {
  clearActiveConversationId,
  persistActiveConversationId,
  readActiveConversationId,
  restoreJustinianoConversation,
} from "./conversation-history.js";
import {
  justinianoDraftCatalog,
  readJustinianoDrafts,
} from "./draft-storage.js";
import { latestCitationReferences } from "./sources.js";
import { searchRecentSimilarDrafts } from "./draft-search.js";
import { normalizeRestoredMessages } from "./history.js";
import {
  JUSTINIANO_LAYOUT_EVENT,
  JUSTINIANO_LAYOUT_STORAGE_KEY,
  persistJustinianoLayoutPreference,
  readJustinianoLayoutPreference,
} from "./layout-preference.js";
import {
  persistResponseModePreference,
  readResponseModePreference,
} from "./response-mode-preference.js";
import "./styles.css";

const AssistantContent = lazy(() => import("./AssistantContent.jsx"));
const DraftCanvas = lazy(() => import("./DraftCanvas.jsx").then((module) => ({
  default: module.DraftCanvas,
})));

class DraftingErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="justiniano-drafting-error" role="alert">
          <FilePenLine size={22} />
          <strong>No pudimos abrir el editor</strong>
          <p>Tu borrador sigue guardado. Intentá abrirlo nuevamente.</p>
          <button type="button" onClick={() => this.setState({ failed: false })}>
            Reintentar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

let localMessageSequence = 0;
const acceptedAttachmentExtensions = new Set([
  "aac",
  "csv",
  "doc",
  "docx",
  "gif",
  "flac",
  "jpeg",
  "jpg",
  "md",
  "m4a",
  "mp3",
  "mp4",
  "mpeg",
  "mpga",
  "odt",
  "oga",
  "ogg",
  "opus",
  "pdf",
  "png",
  "ppt",
  "pptx",
  "rtf",
  "tsv",
  "txt",
  "webp",
  "webm",
  "wav",
  "xls",
  "xlsx",
]);
const maximumAttachmentBytes = 10 * 1024 * 1024;
const dockBreakpoint = 1025;
const resizeBreakpoint = 621;
let dockOwnerSequence = 0;

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

function panelWidthForViewport(width, viewportWidth) {
  const maximum = Math.max(1, Math.floor(viewportWidth * 0.5));
  const minimum = Math.min(320, maximum);
  return Math.round(clamp(Number(width) || 408, minimum, maximum));
}

function panelHeightForViewport(height, viewportHeight) {
  const maximum = Math.max(1, Math.floor(viewportHeight - 110));
  const minimum = Math.min(480, maximum);
  return Math.round(clamp(Number(height) || 680, minimum, maximum));
}

function localId(prefix) {
  localMessageSequence += 1;
  return `${prefix}-${Date.now()}-${localMessageSequence}`;
}

function defaultSignatureSettingsHref(apiUrl) {
  try {
    const api = new URL(apiUrl, typeof window === "undefined"
      ? "https://api.tribunalea.com"
      : window.location.href);
    if (["localhost", "127.0.0.1"].includes(api.hostname)) {
      return "http://localhost:5175/firma";
    }
  } catch {
    // Si la URL de la API no es absoluta, usamos la cuenta pública.
  }
  return "https://account.tribunalea.com/firma";
}

function AssistantMark({ size = 20 }) {
  return (
    <span className="justiniano-mark" aria-hidden="true">
      <Sparkles size={size} strokeWidth={2.2} />
    </span>
  );
}

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function audioFileExtension(mimeType) {
  const normalized = String(mimeType || "").split(";", 1)[0].toLowerCase();
  return ({
    "audio/aac": "aac",
    "audio/mp4": "m4a",
    "audio/mpeg": "mp3",
    "audio/ogg": "ogg",
    "audio/opus": "opus",
    "audio/wav": "wav",
    "audio/webm": "webm",
  })[normalized] || "webm";
}

function supportedRecordingMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  return [
    "audio/webm;codecs=opus",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ].find((mimeType) => MediaRecorder.isTypeSupported?.(mimeType)) || "";
}

function conversationDateLabel(value) {
  const date = new Date(value || 0);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "short",
    ...(date.getFullYear() !== new Date().getFullYear()
      ? { year: "numeric" }
      : {}),
  }).format(date);
}

function ConversationHistory({
  activeConversationId,
  error,
  items,
  loading,
  loadingMore,
  nextCursor,
  onBack,
  onLoadMore,
  onQueryChange,
  onSelect,
  query,
}) {
  return (
    <section className="justiniano-history" aria-label="Historial de conversaciones">
      <header className="justiniano-history-heading">
        <button type="button" onClick={onBack} aria-label="Volver al chat">
          <ArrowLeft size={17} />
        </button>
        <div>
          <strong>Conversaciones</strong>
          <small>Retomá una consulta anterior</small>
        </div>
      </header>
      <label className="justiniano-history-search">
        <Search size={15} />
        <input
          aria-label="Buscar conversaciones"
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Buscar por título o mensaje"
          type="search"
          value={query}
        />
      </label>
      <div className="justiniano-history-list">
        {loading ? (
          <div className="justiniano-history-state">
            <LoaderCircle className="is-spinning" size={19} />
            Cargando conversaciones…
          </div>
        ) : error ? (
          <div className="justiniano-history-state is-error">{error}</div>
        ) : items.length ? (
          <>
            {items.map((conversation) => (
              <button
                className={conversation.id === activeConversationId
                  ? "justiniano-history-item is-active"
                  : "justiniano-history-item"}
                key={conversation.id}
                onClick={() => onSelect(conversation.id)}
                type="button"
              >
                <MessageCircle size={16} />
                <span>
                  <strong>{conversation.title || "Nueva conversación"}</strong>
                  {conversation.preview && <span>{conversation.preview}</span>}
                  <small>
                    {conversationDateLabel(conversation.updatedAt)}
                    {conversation.assignment?.contextLabel
                      ? ` · ${conversation.assignment.contextLabel}`
                      : ""}
                  </small>
                </span>
                <ChevronRight size={15} />
              </button>
            ))}
            {nextCursor && (
              <button
                className="justiniano-history-more"
                disabled={loadingMore}
                onClick={onLoadMore}
                type="button"
              >
                {loadingMore && <LoaderCircle className="is-spinning" size={14} />}
                Ver más
              </button>
            )}
          </>
        ) : (
          <div className="justiniano-history-state is-empty">
            <History size={24} />
            <strong>{query ? "No encontramos conversaciones" : "Todavía no hay conversaciones"}</strong>
            <span>{query
              ? "Probá con otras palabras."
              : "Tus próximas consultas aparecerán acá."}</span>
          </div>
        )}
      </div>
    </section>
  );
}

function ConversationAssignmentDialog({
  assignment,
  busy,
  config,
  error,
  loading,
  onAssign,
  onClose,
  onRemove,
  targets,
}) {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLocaleLowerCase("es");
  const filteredTargets = targets.filter((target) => (
    !normalizedQuery
    || [target.label, target.secondary, target.id]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase("es")
      .includes(normalizedQuery)
  ));
  return (
    <div className="justiniano-assignment-backdrop" role="presentation">
      <section
        aria-labelledby="justiniano-assignment-title"
        aria-modal="true"
        className="justiniano-assignment-dialog"
        role="dialog"
      >
        <header>
          <div>
            <strong id="justiniano-assignment-title">
              Vincular a {config.label || "un expediente"}
            </strong>
            <small>La conversación aparecerá junto a sus escritos.</small>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar">
            <X size={17} />
          </button>
        </header>
        {assignment && (
          <div className="justiniano-assignment-current">
            <Link2 size={15} />
            <span>
              <strong>{assignment.contextLabel || assignment.contextId}</strong>
              <small>{assignment.contextId}</small>
            </span>
            <button disabled={busy} onClick={onRemove} type="button">
              <Unlink size={14} /> Desvincular
            </button>
          </div>
        )}
        <label className="justiniano-history-search">
          <Search size={15} />
          <input
            autoFocus
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Buscar ${config.pluralLabel || "expedientes"}`}
            type="search"
            value={query}
          />
        </label>
        {error && <div className="justiniano-assignment-error">{error}</div>}
        <div className="justiniano-assignment-targets">
          {loading ? (
            <div className="justiniano-history-state">
              <LoaderCircle className="is-spinning" size={18} /> Cargando…
            </div>
          ) : filteredTargets.length ? filteredTargets.map((target) => (
            <button
              disabled={busy || assignment?.contextId === target.id}
              key={target.id}
              onClick={() => onAssign(target)}
              type="button"
            >
              <span>
                <strong>{target.label}</strong>
                <small>{target.secondary || target.id}</small>
              </span>
              {assignment?.contextId === target.id
                ? <Check size={16} />
                : <Link2 size={15} />}
            </button>
          )) : (
            <div className="justiniano-history-state is-empty">
              No encontramos resultados.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function AttachmentChip({
  attachment,
  removable = false,
  onRemove,
  onReuse,
  reuseState = null,
}) {
  const Icon = attachment.kind === "image"
    ? ImageIcon
    : attachment.kind === "audio"
      ? AudioLines
      : FileText;
  return (
    <span className="justiniano-attachment-chip">
      <Icon size={13} />
      <span>
        <strong>{attachment.fileName}</strong>
        {attachment.size ? <small>{formatFileSize(attachment.size)}</small> : null}
      </span>
      {removable && (
        <button
          type="button"
          onClick={() => onRemove(attachment)}
          aria-label={`Quitar ${attachment.fileName}`}
          title="Quitar archivo"
        >
          <X size={12} />
        </button>
      )}
      {onReuse && reuseState !== "unavailable" && (
        <button
          type="button"
          className="justiniano-attachment-reuse"
          onClick={() => onReuse(attachment)}
          disabled={reuseState === "selected"}
          aria-label={reuseState === "selected"
            ? `${attachment.fileName} se usará en el próximo mensaje`
            : `Seguir usando ${attachment.fileName}`}
          title={reuseState === "selected"
            ? "Se usará en el próximo mensaje"
            : "Seguir usando este archivo"}
        >
          {reuseState === "selected" ? <Check size={11} /> : <Paperclip size={11} />}
          {reuseState === "selected" ? "Seleccionado" : "Seguir usando"}
        </button>
      )}
    </span>
  );
}

function ToolResult({ result, renderers, context }) {
  if (!result) return null;
  const rendered = renderers?.renderToolResult?.({
    toolName: result.name,
    data: result.data,
    context,
  });
  if (rendered !== undefined && rendered !== null) {
    return <div className="justiniano-tool-result">{rendered}</div>;
  }
  return null;
}

function Sources({ citations, renderers, context }) {
  if (!citations?.length) return null;

  return (
    <section
      className="justiniano-sources"
      aria-label="Fuentes y referencias de la respuesta"
    >
      <div className="justiniano-sources-title">
        Fuentes y referencias
        <span>{citations.length}</span>
      </div>
      <div className="justiniano-sources-list">
        {citations.map((citation, index) => {
          const rendered = renderers?.renderCitation?.({
            citation,
            index,
            context,
          });
          return (
            <div
              key={citation.evidenceId || citation.id || `${index}-${citation.label}`}
            >
              {rendered || <CitationCard citation={citation} index={index} />}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Confirmation({
  action,
  busy,
  renderers,
  context,
  onConfirm,
  onCancel,
}) {
  const rendered = renderers?.renderConfirmation?.({
    action,
    context,
  });

  return (
    <div className={`justiniano-confirmation is-${action.status}`}>
      {rendered || (
        <p>
          Confirmá si querés que Justiniano ejecute esta acción en el espacio
          seleccionado.
        </p>
      )}
      {action.status === "pending" && (
        <div className="justiniano-confirmation-actions">
          <button
            type="button"
            className="justiniano-secondary-button"
            onClick={onCancel}
            disabled={busy}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="justiniano-primary-button"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? <LoaderCircle className="is-spinning" size={15} /> : <Check size={15} />}
            Confirmar
          </button>
        </div>
      )}
      {action.status === "completed" && (
        <span className="justiniano-action-state">
          <Check size={14} /> Acción realizada
        </span>
      )}
      {action.status === "cancelled" && (
        <span className="justiniano-action-state">Acción cancelada</span>
      )}
      {action.status === "executing" && (
        <span className="justiniano-action-state">
          <LoaderCircle className="is-spinning" size={14} /> Acción en curso
        </span>
      )}
      {action.status === "failed" && (
        <span className="justiniano-action-state is-error">
          No se pudo completar la acción
        </span>
      )}
    </div>
  );
}

function ConversationMessage({
  message,
  renderers,
  context,
  onAction,
  onConfirm,
  onCancel,
  onReuseAttachment,
  attachmentReuseState,
}) {
  const isAssistant = message.role === "assistant";

  return (
    <article
      className={`justiniano-message is-${message.role} ${
        message.status === "error" ? "has-error" : ""
      }`}
    >
      {isAssistant && (
        <span className="justiniano-message-avatar">
          <Bot size={15} />
        </span>
      )}
      <div className="justiniano-message-content">
        {!isAssistant && message.attachments?.length > 0 && (
          <div className="justiniano-message-attachments">
            {message.attachments.map((attachment) => (
              <AttachmentChip
                key={attachment.id}
                attachment={attachment}
                onReuse={onReuseAttachment}
                reuseState={attachmentReuseState?.(attachment) || null}
              />
            ))}
          </div>
        )}
        {message.content && (
          <div className="justiniano-bubble">
            {isAssistant ? (
              <Suspense fallback={message.content}>
                <AssistantContent
                  onAction={onAction}
                  renderLink={renderers?.renderLink}
                >
                  {message.content}
                </AssistantContent>
              </Suspense>
            ) : (
              message.content
            )}
          </div>
        )}
        {message.status === "thinking" && !message.content && (
          <div className="justiniano-thinking" aria-label="Justiniano está pensando">
            <span />
            <span />
            <span />
          </div>
        )}
        {isAssistant && message.statusText && (
          <div className="justiniano-status" role="status" aria-live="polite">
            <LoaderCircle className="is-spinning" size={12} />
            <span>{message.statusText}</span>
          </div>
        )}
        <ToolResult
          result={message.toolResult}
          renderers={renderers}
          context={context}
        />
        {message.confirmation && (
          <Confirmation
            action={message.confirmation}
            busy={message.confirmationBusy}
            renderers={renderers}
            context={context}
            onConfirm={() => onConfirm(message.id, message.confirmation)}
            onCancel={() => onCancel(message.id, message.confirmation)}
          />
        )}
        {message.warning && (
          <p className="justiniano-warning">{message.warning}</p>
        )}
        <Sources
          citations={message.citations}
          renderers={renderers}
          context={context}
        />
        {isAssistant && message.generatedContent && (
          <span className="justiniano-ai-label">
            Explicación generada con IA
          </span>
        )}
      </div>
    </article>
  );
}

export function Justiniano({
  appId,
  appName,
  apiUrl,
  apiBasePath,
  getAccessToken,
  assistantName = "Justiniano",
  poweredBy = null,
  variant = "floating",
  welcome = "Hola, soy Justiniano. ¿En qué puedo ayudarte?",
  suggestions = [],
  renderers,
  context,
  appContext,
  responseModes = [],
  defaultResponseMode,
  allowAttachments = false,
  notebookIds = [],
  notebookScope = "selected",
  requestedSources = [],
  conversationScope = null,
  conversationMetadata = null,
  conversationAssignment = null,
  enableConversationHistory = true,
  enablePageHelp = false,
  signatureSettingsHref,
  onNavigate,
  onRequestClose,
  autoFocus = true,
  initialDraft = null,
  initialView = null,
  openRequest = null,
  onOpenRequestHandled = null,
}) {
  const pageVariant = variant === "page";
  const conversationStorageId = conversationScope
    ? `${appId}:${conversationScope}`
    : appId;
  const resolvedSignatureSettingsHref =
    signatureSettingsHref || defaultSignatureSettingsHref(apiUrl);
  const [open, setOpen] = useState(pageVariant);
  const visible = pageVariant || open;
  const [layoutPreference, setLayoutPreference] = useState(() => (
    readJustinianoLayoutPreference()
  ));
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === "undefined" ? 1440 : window.innerWidth,
    height: typeof window === "undefined" ? 900 : window.innerHeight,
  }));
  const [resizing, setResizing] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversationTitle, setConversationTitle] = useState("");
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [activeView, setActiveView] = useState(() => (
    initialView || (initialDraft ? "drafting" : "chat")
  ));
  const [draftHandoff, setDraftHandoff] = useState(() => (
    initialDraft
      ? {
          handoffId: `initial-draft-${initialDraft.id || appId}`,
          action: "create_draft",
          draftId: initialDraft.id || `initial-${appId}`,
          title: initialDraft.title || "Nueva redacción",
          document: initialDraft.document || initialDraft.content || "",
          references: initialDraft.references || [],
        }
      : null
  ));
  const [responseMode, setResponseMode] = useState(() => (
    readResponseModePreference({
      appId,
      responseModes,
      defaultResponseMode,
    })
  ));
  const [sending, setSending] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState("");
  const [restoreRevision, setRestoreRevision] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyQuery, setHistoryQuery] = useState("");
  const [historyCursor, setHistoryCursor] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [assignmentTargets, setAssignmentTargets] = useState([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentBusy, setAssignmentBusy] = useState(false);
  const [assignmentError, setAssignmentError] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [availableAttachments, setAvailableAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [recordingAudio, setRecordingAudio] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const [draggingFiles, setDraggingFiles] = useState(false);
  const [pageHelp, setPageHelp] = useState(null);
  const [knownActions, setKnownActions] = useState({});
  const [proactiveTip, setProactiveTip] = useState(null);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const dragDepthRef = useRef(0);
  const abortRef = useRef(null);
  const restoreCompletedRef = useRef(false);
  const restoreAbortRef = useRef(null);
  const historyAbortRef = useRef(null);
  const conversationDraftsRef = useRef(new Map());
  const instantScrollRef = useRef(true);
  const previousVisibleRef = useRef(visible);
  const tipTimerRef = useRef(null);
  const resizeRef = useRef(null);
  const layoutPreferenceRef = useRef(layoutPreference);
  const dockOwnerRef = useRef(null);
  if (!dockOwnerRef.current) {
    dockOwnerSequence += 1;
    dockOwnerRef.current = `justiniano-dock-${dockOwnerSequence}`;
  }

  const canDock = !pageVariant && viewport.width >= dockBreakpoint;
  const canResize = !pageVariant && viewport.width >= resizeBreakpoint;
  const docked = canDock && layoutPreference.docked;
  const panelWidth = panelWidthForViewport(
    layoutPreference.width,
    viewport.width,
  );
  const floatingHeight = panelHeightForViewport(
    layoutPreference.floatingHeight,
    viewport.height,
  );
  const dockActive = visible && docked;

  layoutPreferenceRef.current = layoutPreference;

  const commitLayoutPreference = useCallback((layout) => {
    const next = persistJustinianoLayoutPreference(layout);
    layoutPreferenceRef.current = next;
    setLayoutPreference(next);
  }, []);

  const client = useMemo(
    () =>
      createJustinianoClient({
        apiUrl,
        appId,
        apiBasePath,
        conversationTitle: "Nueva conversación",
        conversationScope,
        getAccessToken,
        surface: pageVariant
          ? "full_page_assistant"
          : "floating_assistant",
        conversationMetadata,
      }),
    [
      apiUrl,
      apiBasePath,
      appId,
      assistantName,
      conversationMetadata,
      conversationScope,
      getAccessToken,
      pageVariant,
    ],
  );

  const updateMessage = useCallback((id, updater) => {
    setMessages((current) =>
      current.map((message) =>
        message.id === id ? updater(message) : message,
      ),
    );
  }, []);

  const applyRestoredConversation = useCallback((restored) => {
    if (!restored) return;
    const restoredId = restored.conversation.id;
    persistActiveConversationId(conversationStorageId, restoredId);
    setConversationId(restoredId);
    setConversationTitle(
      restored.conversation.title || "Nueva conversación",
    );
    setActiveAssignment(restored.conversation.assignment || null);
    setAvailableAttachments(restored.attachments || []);
    setAttachments([]);
    instantScrollRef.current = true;
    setMessages(
      normalizeRestoredMessages(
        restored.messages,
        restored.toolActions,
      ),
    );
  }, [conversationStorageId]);

  const activateConversation = useCallback(async (targetConversationId) => {
    if (!targetConversationId || sending || uploadingAttachment || recordingAudio) {
      return;
    }
    if (conversationId) {
      conversationDraftsRef.current.set(conversationId, draft);
    }
    restoreAbortRef.current?.abort();
    const controller = new AbortController();
    restoreAbortRef.current = controller;
    setOpen(true);
    setActiveView("chat");
    setHistoryOpen(false);
    setAssignmentOpen(false);
    setRestoring(true);
    setRestoreError("");
    setDraft(conversationDraftsRef.current.get(targetConversationId) || "");
    try {
      const restored = await client.loadConversation(targetConversationId, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      restoreCompletedRef.current = true;
      applyRestoredConversation(restored);
    } catch (error) {
      if (controller.signal.aborted || error.name === "AbortError") return;
      setRestoreError(
        error.message || "No pude recuperar esa conversación.",
      );
    } finally {
      if (!controller.signal.aborted) setRestoring(false);
    }
  }, [
    applyRestoredConversation,
    client,
    conversationId,
    draft,
    recordingAudio,
    sending,
    uploadingAttachment,
  ]);

  const loadConversationHistory = useCallback(async ({
    append = false,
    cursor = null,
    query = historyQuery,
  } = {}) => {
    historyAbortRef.current?.abort();
    const controller = new AbortController();
    historyAbortRef.current = controller;
    if (append) setHistoryLoadingMore(true);
    else setHistoryLoading(true);
    setHistoryError("");
    try {
      const result = await client.listConversationHistory({
        limit: 30,
        cursor,
        query: query.trim() || undefined,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setHistoryItems((current) => append
        ? [...current, ...(result.items || [])]
        : (result.items || []));
      setHistoryCursor(result.nextCursor || null);
    } catch (error) {
      if (controller.signal.aborted || error.name === "AbortError") return;
      setHistoryError(
        error.message || "No pudimos cargar tus conversaciones.",
      );
    } finally {
      if (!controller.signal.aborted) {
        setHistoryLoading(false);
        setHistoryLoadingMore(false);
      }
    }
  }, [client, historyQuery]);

  const openAssignmentDialog = useCallback(async () => {
    if (!conversationAssignment || !conversationId) return;
    setAssignmentOpen(true);
    setAssignmentError("");
    setAssignmentLoading(true);
    try {
      const loaded = await conversationAssignment.loadTargets();
      const candidates = [
        ...(conversationAssignment.activeTarget
          ? [conversationAssignment.activeTarget]
          : []),
        ...(loaded || []),
      ];
      setAssignmentTargets([
        ...new Map(candidates.map((target) => [target.id, target])).values(),
      ]);
    } catch (error) {
      setAssignmentError(
        error.message || "No pudimos cargar los expedientes.",
      );
    } finally {
      setAssignmentLoading(false);
    }
  }, [conversationAssignment, conversationId]);

  const assignActiveConversation = useCallback(async (target) => {
    if (!conversationId || !conversationAssignment) return;
    setAssignmentBusy(true);
    setAssignmentError("");
    try {
      const assignment = await client.assignConversation(conversationId, {
        contextType: conversationAssignment.contextType || "expediente",
        contextId: target.id,
      });
      setActiveAssignment(assignment);
      setHistoryItems((current) => current.map((item) => (
        item.id === conversationId ? { ...item, assignment } : item
      )));
      window.dispatchEvent(new CustomEvent(
        "justiniano:conversation-assignment-changed",
        { detail: { appId, conversationId, assignment } },
      ));
      setAssignmentOpen(false);
    } catch (error) {
      setAssignmentError(
        error.message || "No pudimos vincular la conversación.",
      );
    } finally {
      setAssignmentBusy(false);
    }
  }, [appId, client, conversationAssignment, conversationId]);

  const removeActiveConversationAssignment = useCallback(async () => {
    if (!conversationId) return;
    setAssignmentBusy(true);
    setAssignmentError("");
    try {
      await client.removeConversationAssignment(conversationId);
      setActiveAssignment(null);
      setHistoryItems((current) => current.map((item) => (
        item.id === conversationId ? { ...item, assignment: null } : item
      )));
      window.dispatchEvent(new CustomEvent(
        "justiniano:conversation-assignment-changed",
        { detail: { appId, conversationId, assignment: null } },
      ));
      setAssignmentOpen(false);
    } catch (error) {
      setAssignmentError(
        error.message || "No pudimos desvincular la conversación.",
      );
    } finally {
      setAssignmentBusy(false);
    }
  }, [appId, client, conversationId]);

  useEffect(() => {
    if (pageVariant) return undefined;
    const updateViewport = () => setViewport({
      width: window.innerWidth,
      height: window.innerHeight,
    });
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, [pageVariant]);

  useEffect(() => {
    if (pageVariant) return undefined;
    const sync = (event) => {
      if (event?.type === "storage" && event.key !== JUSTINIANO_LAYOUT_STORAGE_KEY) {
        return;
      }
      const next = readJustinianoLayoutPreference();
      layoutPreferenceRef.current = next;
      setLayoutPreference(next);
    };
    const syncWhenVisible = () => {
      if (document.visibilityState === "visible") sync();
    };
    window.addEventListener(JUSTINIANO_LAYOUT_EVENT, sync);
    window.addEventListener("focus", sync);
    window.addEventListener("pageshow", sync);
    window.addEventListener("storage", sync);
    document.addEventListener("visibilitychange", syncWhenVisible);
    return () => {
      window.removeEventListener(JUSTINIANO_LAYOUT_EVENT, sync);
      window.removeEventListener("focus", sync);
      window.removeEventListener("pageshow", sync);
      window.removeEventListener("storage", sync);
      document.removeEventListener("visibilitychange", syncWhenVisible);
    };
  }, [pageVariant]);

  useEffect(() => {
    if (!dockActive) return undefined;
    const root = document.documentElement;
    const owner = dockOwnerRef.current;
    root.dataset.justinianoDockOwner = owner;
    root.classList.add("justiniano-dock-active");
    return () => {
      if (root.dataset.justinianoDockOwner !== owner) return;
      root.classList.remove("justiniano-dock-active");
      root.style.removeProperty("--justiniano-dock-width");
      delete root.dataset.justinianoDockOwner;
    };
  }, [dockActive]);

  useEffect(() => {
    if (!dockActive) return;
    const root = document.documentElement;
    if (root.dataset.justinianoDockOwner !== dockOwnerRef.current) return;
    root.style.setProperty("--justiniano-dock-width", `${panelWidth}px`);
  }, [dockActive, panelWidth]);

  useEffect(() => {
    if (pageVariant) return undefined;
    const resizePanel = (event) => {
      const operation = resizeRef.current;
      if (!operation) return;
      let width = operation.width;
      let floatingPanelHeight = operation.height;
      if (operation.mode === "width" || operation.mode === "both") {
        width = panelWidthForViewport(
          operation.startWidth + operation.startX - event.clientX,
          window.innerWidth,
        );
      }
      if (operation.mode === "height" || operation.mode === "both") {
        floatingPanelHeight = panelHeightForViewport(
          operation.startHeight + operation.startY - event.clientY,
          window.innerHeight,
        );
      }
      operation.width = width;
      operation.height = floatingPanelHeight;
      const next = {
        ...layoutPreferenceRef.current,
        width,
        floatingHeight: floatingPanelHeight,
      };
      layoutPreferenceRef.current = next;
      setLayoutPreference(next);
    };
    const finishResize = () => {
      const operation = resizeRef.current;
      if (!operation) return;
      resizeRef.current = null;
      setResizing(false);
      document.documentElement.classList.remove("justiniano-resizing");
      commitLayoutPreference({
        ...layoutPreferenceRef.current,
        width: operation.width,
        floatingHeight: operation.height,
      });
    };
    window.addEventListener("pointermove", resizePanel);
    window.addEventListener("pointerup", finishResize);
    window.addEventListener("pointercancel", finishResize);
    return () => {
      window.removeEventListener("pointermove", resizePanel);
      window.removeEventListener("pointerup", finishResize);
      window.removeEventListener("pointercancel", finishResize);
      document.documentElement.classList.remove("justiniano-resizing");
    };
  }, [commitLayoutPreference, pageVariant]);

  useEffect(() => {
    if (!visible || restoreCompletedRef.current) return undefined;
    const controller = new AbortController();
    restoreAbortRef.current = controller;
    setRestoring(true);
    setRestoreError("");

    restoreJustinianoConversation(client, {
      preferredConversationId: readActiveConversationId(conversationStorageId),
      restoreLatest: !conversationScope,
      signal: controller.signal,
    })
      .then((restored) => {
        if (controller.signal.aborted) return;
        restoreCompletedRef.current = true;
        if (!restored) {
          clearActiveConversationId(conversationStorageId);
          setConversationTitle("");
          setActiveAssignment(null);
          return;
        }
        applyRestoredConversation(restored);
      })
      .catch((error) => {
        if (controller.signal.aborted || error.name === "AbortError") return;
        setRestoreError(
          error.message || "No pude recuperar la conversación anterior.",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setRestoring(false);
      });

    return () => {
      controller.abort();
      if (restoreAbortRef.current === controller) {
        restoreAbortRef.current = null;
      }
    };
  }, [
    applyRestoredConversation,
    client,
    conversationScope,
    conversationStorageId,
    restoreRevision,
    visible,
  ]);

  useEffect(() => {
    if (!historyOpen) return undefined;
    const timeout = window.setTimeout(() => {
      void loadConversationHistory({ query: historyQuery });
    }, historyQuery ? 250 : 0);
    return () => window.clearTimeout(timeout);
  }, [historyOpen, historyQuery, loadConversationHistory]);

  useEffect(() => () => {
    historyAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!enablePageHelp) return undefined;
    let active = true;
    window.clearTimeout(tipTimerRef.current);
    setProactiveTip(null);
    client.getPageHelp(appContext?.route || "/")
      .then((help) => {
        if (!active) return;
        setPageHelp(help);
        if (!help) return;
        setKnownActions((current) => ({
          ...current,
          ...Object.fromEntries(
            (help.targets || []).map((action) => [action.id, action]),
          ),
        }));
        if (pageVariant || !help.tip) return;
        const storageKey = [
          "justiniano-tip",
          appId,
          help.tip.id,
          help.tip.version,
        ].join(":");
        let seen = false;
        try {
          seen = window.localStorage.getItem(storageKey) === "seen";
        } catch {
          seen = false;
        }
        if (!seen) {
          tipTimerRef.current = window.setTimeout(() => {
            if (active) setProactiveTip({ ...help.tip, storageKey });
          }, 700);
        }
      })
      .catch(() => {
        if (active) setPageHelp(null);
      });
    return () => {
      active = false;
      window.clearTimeout(tipTimerRef.current);
    };
  }, [
    appContext?.route,
    appId,
    client,
    enablePageHelp,
    pageVariant,
  ]);

  useEffect(() => {
    if (!visible) return undefined;
    const focusTimer = autoFocus
      ? window.setTimeout(() => inputRef.current?.focus(), 100)
      : null;
    const onKeyDown = (event) => {
      if (!pageVariant && event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [autoFocus, pageVariant, visible]);

  useEffect(() => {
    const compose = (event) => {
      const detail = event.detail || {};
      if (detail.appId && detail.appId !== appId) return;
      const prompt = String(detail.prompt || "").trim();
      if (!prompt) return;
      if (!pageVariant) setOpen(true);
      setActiveView("chat");
      setDraft(prompt);
    };
    window.addEventListener("justiniano:compose", compose);
    return () => window.removeEventListener("justiniano:compose", compose);
  }, [appId, pageVariant]);

  useEffect(() => {
    const openConversation = (event) => {
      const detail = event.detail || {};
      if (detail.appId && detail.appId !== appId) return;
      if (!detail.conversationId) return;
      void activateConversation(String(detail.conversationId));
    };
    window.addEventListener(
      "justiniano:open-conversation",
      openConversation,
    );
    return () => window.removeEventListener(
      "justiniano:open-conversation",
      openConversation,
    );
  }, [activateConversation, appId]);

  useEffect(() => {
    if (pageVariant || !openRequest) return;
    setOpen(true);
    if (["chat", "drafting"].includes(openRequest.view)) {
      setActiveView(openRequest.view);
    }
    onOpenRequestHandled?.();
  }, [onOpenRequestHandled, openRequest, pageVariant]);

  useLayoutEffect(() => {
    const justOpened = visible && !previousVisibleRef.current;
    previousVisibleRef.current = visible;
    if (!visible || !scrollRef.current) return;

    if (justOpened || instantScrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      instantScrollRef.current = false;
      return;
    }

    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [activeView, messages, visible]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      const recorder = audioRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.stop();
      }
      audioStreamRef.current?.getTracks().forEach((track) => track.stop());
    },
    [],
  );

  async function ensureConversation() {
    if (conversationId) return conversationId;
    const conversation = await client.createConversation();
    restoreCompletedRef.current = true;
    persistActiveConversationId(conversationStorageId, conversation.id);
    setConversationId(conversation.id);
    setConversationTitle(conversation.title || "Nueva conversación");
    setActiveAssignment(null);
    return conversation.id;
  }

  async function editDraftDocument(input, { activeDraft = null } = {}) {
    const id = await ensureConversation();
    const similarDrafts = String(input.plainText || "").trim()
      ? []
      : searchRecentSimilarDrafts(
        readJustinianoDrafts(appId),
        input.instruction,
        { excludeDraftId: activeDraft?.id || null },
      );
    return client.editDraft(id, {
      ...input,
      draftContext: {
        drafts: justinianoDraftCatalog(appId),
        activeDraftId: activeDraft?.id || null,
        activeDraftTitle: activeDraft?.title || null,
        activeDraftIncludesSignature: activeDraft?.includeSignature === true,
        references: normalizeCitationReferences([
          ...(activeDraft?.references || []),
          ...latestCitationReferences(messages),
        ]).slice(0, 50),
        similarDrafts,
      },
    }, { appContext });
  }

  async function send(content = draft, { skipDraftTool = false } = {}) {
    const normalized = String(content || "").trim();
    const hasAudioInstruction = attachments.some(
      (attachment) => attachment.kind === "audio",
    );
    if (
      (!normalized && !hasAudioInstruction)
      || sending
      || restoring
      || uploadingAttachment
      || recordingAudio
    ) return;

    const userMessage = {
      id: localId("user"),
      role: "user",
      status: "completed",
      content: normalized,
      attachments: [...attachments],
    };
    const assistantId = localId("assistant");
    setMessages((current) => [
      ...current,
      userMessage,
      {
        id: assistantId,
        role: "assistant",
        status: "thinking",
        content: "",
        citations: [],
        statusText: "Iniciando consulta…",
      },
    ]);
    setDraft("");
    setSending(true);

    try {
      const id = await ensureConversation();
      const controller = new AbortController();
      abortRef.current = controller;

      await client.sendMessage(
        id,
        normalized,
        (event, data) => {
          if (
            event === "tool.completed"
            && data?.name === "justiniano.redaction"
          ) {
            setDraftHandoff({
              ...data.data,
              handoffId: localId("draft-handoff"),
            });
            setActiveView("drafting");
          }
          if (event === "completed" && data?.conversationTitle) {
            setConversationTitle(data.conversationTitle);
          }
          updateMessage(assistantId, (message) => {
            if (event === "status") {
              return {
                ...message,
                status: "thinking",
                statusText: data.message || "Procesando la consulta…",
                statusStage: data.stage || null,
              };
            }
            if (event === "answer.delta") {
              return {
                ...message,
                status: "streaming",
                content: `${message.content}${data.delta || ""}`,
                statusText: null,
              };
            }
            if (event === "tool.started") {
              const progressMessage = typeof data.message === "string"
                ? data.message.trim()
                : "";
              const customStatus = renderers?.getToolStatus?.({
                toolName: data.name,
                context,
              });
              return {
                ...message,
                status: "thinking",
                activeTool: data.name,
                statusText:
                  progressMessage
                  || customStatus
                  || (data.name === "justiniano.redaction"
                    ? "Preparando la redacción…"
                    : `Consultando información de ${appName}…`),
              };
            }
            if (event === "tool.completed") {
              return { ...message, toolResult: data };
            }
            if (event === "confirmation.required") {
              return {
                ...message,
                confirmation: { ...data, status: "pending" },
              };
            }
            if (event === "citation") {
              if (
                message.citations?.some(
                  (citation) =>
                    citation.evidenceId &&
                    citation.evidenceId === data.evidenceId,
                )
              ) {
                return message;
              }
              return {
                ...message,
                citations: [...(message.citations || []), data],
              };
            }
            if (event === "warning") {
              return { ...message, warning: data.message };
            }
            if (event === "completed") {
              return {
                ...message,
                status: "completed",
                serverId: data.messageId,
                statusText: null,
                generatedContent: Boolean(data.generatedContent),
                evidenceMode: data.evidenceMode || null,
              };
            }
            if (event === "error") {
              return {
                ...message,
                status: "error",
                content:
                  data.message ||
                  "No pude completar la consulta. Intentá nuevamente.",
                statusText: null,
              };
            }
            return message;
          });
        },
        {
          signal: controller.signal,
          appContext,
          responseMode,
          attachmentIds: attachments.map((attachment) => attachment.id),
          notebookIds,
          notebookScope,
          requestedSources,
          draftContext: skipDraftTool
            ? undefined
            : {
                drafts: justinianoDraftCatalog(appId),
                references: latestCitationReferences(messages),
              },
        },
      );
      setAttachments([]);
    } catch (error) {
      if (error.name !== "AbortError") {
        updateMessage(assistantId, (message) => ({
          ...message,
          status: "error",
          content:
            error.message || "No pude completar la consulta. Intentá nuevamente.",
          statusText: null,
        }));
      }
    } finally {
      abortRef.current = null;
      setSending(false);
    }
  }

  function stopAudioRecording() {
    const recorder = audioRecorderRef.current;
    if (recorder?.state && recorder.state !== "inactive") recorder.stop();
  }

  async function startAudioRecording() {
    if (
      sending
      || restoring
      || uploadingAttachment
      || recordingAudio
      || attachments.length >= 5
    ) return;
    if (
      typeof navigator === "undefined"
      || !navigator.mediaDevices?.getUserMedia
      || typeof MediaRecorder === "undefined"
    ) {
      setAttachmentError("Este navegador no permite grabar audio.");
      return;
    }
    setAttachmentError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      const mimeType = supportedRecordingMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      audioStreamRef.current = stream;
      audioRecorderRef.current = recorder;
      audioChunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data?.size) audioChunksRef.current.push(event.data);
      };
      recorder.onerror = () => {
        setAttachmentError("No pudimos grabar el audio. Intentá nuevamente.");
      };
      recorder.onstop = () => {
        const chunks = audioChunksRef.current;
        const recordedMimeType = recorder.mimeType
          || chunks[0]?.type
          || mimeType
          || "audio/webm";
        const blob = new Blob(chunks, { type: recordedMimeType });
        audioRecorderRef.current = null;
        audioChunksRef.current = [];
        audioStreamRef.current?.getTracks().forEach((track) => track.stop());
        audioStreamRef.current = null;
        setRecordingAudio(false);
        if (!blob.size) {
          setAttachmentError("La grabación quedó vacía. Intentá nuevamente.");
          return;
        }
        const extension = audioFileExtension(recordedMimeType);
        const file = new File(
          [blob],
          `mensaje-justiniano-${Date.now()}.${extension}`,
          { type: recordedMimeType },
        );
        void uploadFiles([file]);
      };
      recorder.start();
      setRecordingAudio(true);
    } catch (error) {
      audioStreamRef.current?.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
      audioRecorderRef.current = null;
      setRecordingAudio(false);
      setAttachmentError(
        error?.name === "NotAllowedError"
          ? "Necesitamos permiso para usar el micrófono."
          : "No pudimos iniciar la grabación de audio.",
      );
    }
  }

  async function uploadFiles(fileList) {
    const files = [...(fileList || [])];
    if (!files.length || uploadingAttachment) return;
    const supportedFiles = files.filter((file) => {
      const extension = file.name.split(".").at(-1)?.toLowerCase();
      return (
        acceptedAttachmentExtensions.has(extension)
        && file.size <= maximumAttachmentBytes
      );
    });
    if (!supportedFiles.length) {
      setAttachmentError(
        "Los archivos deben tener un formato compatible y pesar hasta 10 MB.",
      );
      return;
    }
    const availableSlots = Math.max(0, 5 - attachments.length);
    if (!availableSlots) {
      setAttachmentError(
        "Podés mantener hasta 5 archivos activos por conversación.",
      );
      return;
    }
    setUploadingAttachment(true);
    setAttachmentError("");
    try {
      const id = await ensureConversation();
      const uploaded = [];
      const failures = [];
      for (const file of supportedFiles.slice(0, availableSlots)) {
        try {
          uploaded.push(await client.uploadAttachment(id, file));
        } catch (error) {
          failures.push(error.message || file.name);
        }
      }
      if (uploaded.length) {
        setAvailableAttachments((current) => {
          const byId = new Map(current.map((attachment) => [
            attachment.id,
            attachment,
          ]));
          for (const attachment of uploaded) byId.set(attachment.id, attachment);
          return [...byId.values()];
        });
        setAttachments((current) => {
          const byId = new Map(current.map((attachment) => [
            attachment.id,
            attachment,
          ]));
          for (const attachment of uploaded) {
            byId.set(attachment.id, attachment);
          }
          return [...byId.values()];
        });
      }
      if (failures.length) {
        setAttachmentError(
          failures.length === 1
            ? failures[0]
            : `No se pudieron adjuntar ${failures.length} archivos.`,
        );
      } else if (supportedFiles.length > availableSlots) {
        setAttachmentError(
          `Se adjuntaron ${availableSlots} archivos; el máximo activo es 5.`,
        );
      } else if (supportedFiles.length !== files.length) {
        setAttachmentError(
          "Se ignoraron archivos incompatibles o mayores a 10 MB.",
        );
      }
    } catch (error) {
      setAttachmentError(
        error.message || "No pudimos adjuntar el archivo.",
      );
    } finally {
      setUploadingAttachment(false);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }

  function beginFileDrag(event) {
    if (
      !allowAttachments
      || !Array.from(event.dataTransfer?.types || []).includes("Files")
    ) {
      return;
    }
    event.preventDefault();
    if (uploadingAttachment || attachments.length >= 5) {
      setAttachmentError(
        "Podés mantener hasta 5 archivos activos por conversación.",
      );
      return;
    }
    dragDepthRef.current += 1;
    setDraggingFiles(true);
  }

  function continueFileDrag(event) {
    if (
      !allowAttachments
      || !Array.from(event.dataTransfer?.types || []).includes("Files")
    ) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect =
      uploadingAttachment || attachments.length >= 5
        ? "none"
        : "copy";
  }

  function endFileDrag(event) {
    if (!allowAttachments) return;
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDraggingFiles(false);
  }

  function dropFiles(event) {
    if (!allowAttachments) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setDraggingFiles(false);
    if (uploadingAttachment || attachments.length >= 5) return;
    void uploadFiles(event.dataTransfer?.files);
  }

  function removeAttachment(attachment) {
    if (uploadingAttachment) return;
    setAttachmentError("");
    setAttachments((current) =>
      current.filter((item) => item.id !== attachment.id),
    );
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  function reuseAttachment(attachment) {
    const available = availableAttachments.find(({ id }) => id === attachment.id);
    if (!available) {
      setAttachmentError("Este archivo ya no está disponible. Volvé a adjuntarlo para usarlo nuevamente.");
      return;
    }
    if (attachments.some(({ id }) => id === available.id)) return;
    if (attachments.length >= 5) {
      setAttachmentError("Podés usar hasta 5 archivos en el próximo mensaje.");
      return;
    }
    setAttachments((current) => [...current, available]);
    setAttachmentError("");
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  function attachmentReuseState(attachment) {
    if (attachments.some(({ id }) => id === attachment.id)) return "selected";
    return availableAttachments.some(({ id }) => id === attachment.id)
      ? "available"
      : "unavailable";
  }

  async function confirmAction(messageId, action) {
    if (!conversationId) return;
    updateMessage(messageId, (message) => ({
      ...message,
      confirmationBusy: true,
    }));
    try {
      const result = await client.confirmAction(conversationId, action.actionId);
      updateMessage(messageId, (message) => ({
        ...message,
        confirmationBusy: false,
        confirmation: { ...message.confirmation, status: "completed" },
      }));
      setMessages((current) => [
        ...current,
        {
          id: localId("assistant"),
          role: "assistant",
          status: "completed",
          content: result.message?.content || "La acción se completó correctamente.",
          toolResult: result.action?.output
            ? { name: result.action.toolName, data: result.action.output }
            : null,
        },
      ]);
    } catch (error) {
      updateMessage(messageId, (message) => ({
        ...message,
        confirmationBusy: false,
        warning: error.message,
      }));
    }
  }

  async function cancelAction(messageId, action) {
    if (!conversationId) return;
    updateMessage(messageId, (message) => ({
      ...message,
      confirmationBusy: true,
    }));
    try {
      await client.cancelAction(conversationId, action.actionId);
      updateMessage(messageId, (message) => ({
        ...message,
        confirmationBusy: false,
        confirmation: { ...message.confirmation, status: "cancelled" },
      }));
    } catch (error) {
      updateMessage(messageId, (message) => ({
        ...message,
        confirmationBusy: false,
        warning: error.message,
      }));
    }
  }

  function newConversation() {
    abortRef.current?.abort();
    restoreAbortRef.current?.abort();
    restoreCompletedRef.current = true;
    clearActiveConversationId(conversationStorageId);
    instantScrollRef.current = true;
    setConversationId(null);
    setConversationTitle("");
    setActiveAssignment(null);
    setMessages([]);
    setDraft("");
    setSending(false);
    setRestoring(false);
    setRestoreError("");
    setAttachments([]);
    setAvailableAttachments([]);
    setAttachmentError("");
    setUploadingAttachment(false);
    setDraggingFiles(false);
    setHistoryOpen(false);
    setAssignmentOpen(false);
    dragDepthRef.current = 0;
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  function retryConversationRestore() {
    restoreCompletedRef.current = false;
    setRestoreRevision((current) => current + 1);
  }

  function dismissProactiveTip() {
    if (proactiveTip?.storageKey) {
      try {
        window.localStorage.setItem(proactiveTip.storageKey, "seen");
      } catch {
        // The tip remains dismissible when browser storage is unavailable.
      }
    }
    setProactiveTip(null);
  }

  function focusUiTarget(targetName) {
    if (!targetName) return;
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const target = [...document.querySelectorAll("[data-justiniano-target]")]
        .find((element) => element.dataset.justinianoTarget === targetName);
      if (!target && Date.now() - startedAt < 2_500) return;
      window.clearInterval(timer);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof target.focus === "function") {
        target.focus({ preventScroll: true });
      }
      target.classList.add("justiniano-target-highlight");
      window.setTimeout(() => {
        target.classList.remove("justiniano-target-highlight");
      }, 1_800);
    }, 60);
  }

  function runUiAction(actionId) {
    const action = knownActions[actionId];
    if (!action) return;
    dismissProactiveTip();
    if (action.route && action.route !== appContext?.route) {
      onNavigate?.(action.route);
    }
    focusUiTarget(action.target);
  }

  function beginResize(mode, event) {
    if (!canResize || event.button !== 0) return;
    const bounds = panelRef.current?.getBoundingClientRect();
    if (!bounds) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    resizeRef.current = {
      mode,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: bounds.width,
      startHeight: bounds.height,
      width: panelWidth,
      height: floatingHeight,
    };
    setResizing(true);
    document.documentElement.classList.add("justiniano-resizing");
  }

  function resizeWithKeyboard(axis, event) {
    const direction = axis === "width"
      ? { ArrowLeft: 1, ArrowRight: -1 }[event.key]
      : { ArrowUp: 1, ArrowDown: -1 }[event.key];
    if (!direction) return;
    event.preventDefault();
    const amount = event.shiftKey ? 32 : 16;
    commitLayoutPreference({
      ...layoutPreferenceRef.current,
      width: axis === "width"
        ? panelWidthForViewport(panelWidth + direction * amount, viewport.width)
        : panelWidth,
      floatingHeight: axis === "height"
        ? panelHeightForViewport(
          floatingHeight + direction * amount,
          viewport.height,
        )
        : floatingHeight,
    });
  }

  function toggleDock() {
    commitLayoutPreference({
      ...layoutPreferenceRef.current,
      docked: !layoutPreference.docked,
      width: panelWidth,
      floatingHeight,
    });
  }

  const effectiveWelcome = pageHelp?.welcome || welcome;
  const effectiveSuggestions = pageHelp?.suggestions?.length
    ? pageHelp.suggestions
    : suggestions;

  return (
    <div
      className={[
        "justiniano",
        visible ? "is-open" : "",
        pageVariant ? "is-page" : "",
        dockActive ? "is-docked" : "",
        resizing ? "is-resizing" : "",
      ].filter(Boolean).join(" ")}
      style={pageVariant ? undefined : {
        "--justiniano-panel-width": `${panelWidth}px`,
        "--justiniano-panel-height": `${floatingHeight}px`,
      }}
    >
      {visible && (
        <section
          id={`justiniano-panel-${appId}`}
          ref={panelRef}
          className="justiniano-panel"
          role={pageVariant ? "region" : "dialog"}
          aria-label={`${assistantName} en ${appName}`}
        >
          {canResize && (
            <div
              className="justiniano-resize-handle justiniano-resize-handle--width"
              role="separator"
              tabIndex={0}
              aria-label="Cambiar el ancho de Justiniano"
              aria-orientation="vertical"
              aria-valuemin={Math.min(320, Math.floor(viewport.width * 0.5))}
              aria-valuemax={Math.floor(viewport.width * 0.5)}
              aria-valuenow={panelWidth}
              onPointerDown={(event) => beginResize("width", event)}
              onKeyDown={(event) => resizeWithKeyboard("width", event)}
            />
          )}
          {canResize && !docked && (
            <>
              <div
                className="justiniano-resize-handle justiniano-resize-handle--height"
                role="separator"
                tabIndex={0}
                aria-label="Cambiar la altura de Justiniano"
                aria-orientation="horizontal"
                aria-valuemin={Math.min(480, viewport.height - 110)}
                aria-valuemax={Math.max(1, viewport.height - 110)}
                aria-valuenow={floatingHeight}
                onPointerDown={(event) => beginResize("height", event)}
                onKeyDown={(event) => resizeWithKeyboard("height", event)}
              />
              <div
                className="justiniano-resize-handle justiniano-resize-handle--corner"
                aria-hidden="true"
                onPointerDown={(event) => beginResize("both", event)}
              />
            </>
          )}
          <header className="justiniano-header">
            <div className="justiniano-identity">
              <AssistantMark />
              <span>
                <strong>{historyOpen ? "Conversaciones" : assistantName}</strong>
                <small>{historyOpen
                  ? `Historial de ${appName}`
                  : conversationTitle || `Asistente de ${appName}`}</small>
              </span>
            </div>
            <div className="justiniano-header-actions">
              {activeView === "chat" && !historyOpen && conversationAssignment && conversationId && (
                <button
                  className={activeAssignment ? "is-active" : ""}
                  disabled={sending || restoring}
                  type="button"
                  onClick={() => { void openAssignmentDialog(); }}
                  aria-label={activeAssignment
                    ? "Cambiar expediente vinculado"
                    : "Vincular a un expediente"}
                  title={activeAssignment
                    ? `Vinculada a ${activeAssignment.contextLabel || activeAssignment.contextId}`
                    : "Vincular a un expediente"}
                >
                  <Link2 size={17} />
                </button>
              )}
              {activeView === "chat" && !historyOpen && (
                <button
                  type="button"
                  onClick={newConversation}
                  disabled={sending || restoring}
                  aria-label="Nueva conversación"
                  title="Nueva conversación"
                >
                  <Plus size={18} />
                </button>
              )}
              {activeView === "chat" && enableConversationHistory && (
                <button
                  className={historyOpen ? "is-active" : ""}
                  type="button"
                  onClick={() => setHistoryOpen((current) => !current)}
                  disabled={sending || restoring}
                  aria-label={historyOpen
                    ? "Volver al chat"
                    : "Ver conversaciones anteriores"}
                  title={historyOpen ? "Volver al chat" : "Historial"}
                >
                  <History size={18} />
                </button>
              )}
              {!pageVariant && canDock && (
                <button
                  type="button"
                  onClick={toggleDock}
                  aria-label={docked
                    ? "Desacoplar Justiniano"
                    : "Acoplar Justiniano a la derecha"}
                  title={docked
                    ? "Usar como ventana flotante"
                    : "Acoplar a la derecha"}
                >
                  {docked
                    ? <PanelRightClose size={18} />
                    : <PanelRightOpen size={18} />}
                </button>
              )}
              {(!pageVariant || onRequestClose) && (
                <button
                  type="button"
                  onClick={() => {
                    if (onRequestClose) onRequestClose();
                    else {
                      setOpen(false);
                      triggerRef.current?.focus();
                    }
                  }}
                  aria-label={`Cerrar ${assistantName}`}
                >
                  <X size={19} />
                </button>
              )}
            </div>
          </header>

          {!historyOpen && (
          <nav className="justiniano-tabs" aria-label="Espacio de Justiniano">
            <button
              aria-current={activeView === "chat" ? "page" : undefined}
              className={activeView === "chat" ? "is-active" : ""}
              onClick={() => setActiveView("chat")}
              type="button"
            >
              <MessageCircle size={16} />
              Chat
            </button>
            <button
              aria-current={activeView === "drafting" ? "page" : undefined}
              className={activeView === "drafting" ? "is-active" : ""}
              onClick={() => setActiveView("drafting")}
              type="button"
            >
              <FilePenLine size={16} />
              Redacción
            </button>
          </nav>
          )}

          {historyOpen && (
            <ConversationHistory
              activeConversationId={conversationId}
              error={historyError}
              items={historyItems}
              loading={historyLoading}
              loadingMore={historyLoadingMore}
              nextCursor={historyCursor}
              onBack={() => setHistoryOpen(false)}
              onLoadMore={() => void loadConversationHistory({
                append: true,
                cursor: historyCursor,
              })}
              onQueryChange={setHistoryQuery}
              onSelect={(id) => { void activateConversation(id); }}
              query={historyQuery}
            />
          )}

          {!historyOpen && activeView === "chat" && (
          <div className="justiniano-conversation" ref={scrollRef}>
            {restoring && (
              <div className="justiniano-restoring" role="status">
                <LoaderCircle className="is-spinning" size={20} />
                <span>Recuperando tu conversación…</span>
              </div>
            )}
            {!restoring && messages.length === 0 && (
              <div className="justiniano-welcome">
                <AssistantMark size={23} />
                <h2>Hola, soy {assistantName}</h2>
                <p>{effectiveWelcome}</p>
                {restoreError && (
                  <div className="justiniano-restore-error" role="alert">
                    <span>{restoreError}</span>
                    <button type="button" onClick={retryConversationRestore}>
                      Reintentar
                    </button>
                  </div>
                )}
                {effectiveSuggestions.length > 0 && (
                  <div className="justiniano-suggestions">
                    {effectiveSuggestions.map((suggestion) => (
                      <button
                        type="button"
                        key={suggestion}
                        onClick={() => send(suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {messages.map((message) => (
              <ConversationMessage
                key={message.id}
                message={message}
                renderers={renderers}
                context={context}
                onAction={runUiAction}
                onConfirm={confirmAction}
                onCancel={cancelAction}
                onReuseAttachment={allowAttachments ? reuseAttachment : null}
                attachmentReuseState={allowAttachments ? attachmentReuseState : null}
              />
            ))}
          </div>
          )}

          {!historyOpen && activeView === "chat" && (
          <form
            className={[
              "justiniano-composer",
              allowAttachments ? "has-attachment-control" : "",
              responseModes.length > 0
                ? "has-response-mode"
                : "",
              draggingFiles ? "is-dragging-files" : "",
            ].filter(Boolean).join(" ")}
            onDragEnter={beginFileDrag}
            onDragOver={continueFileDrag}
            onDragLeave={endFileDrag}
            onDrop={dropFiles}
            onSubmit={(event) => {
              event.preventDefault();
              send();
            }}
          >
            {allowAttachments && attachments.length > 0 && (
              <div
                className="justiniano-active-attachments"
                aria-label="Archivos para el próximo mensaje"
              >
                <span className="justiniano-active-attachments-label">
                  Para el próximo mensaje
                </span>
                {attachments.map((attachment) => (
                  <AttachmentChip
                    key={attachment.id}
                    attachment={attachment}
                    removable
                    onRemove={removeAttachment}
                  />
                ))}
              </div>
            )}
            <div className="justiniano-composer-bar">
              {allowAttachments && (
                <>
                  <input
                    ref={fileInputRef}
                    className="justiniano-file-input"
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.md,.csv,.tsv,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp,.mp3,.wav,.m4a,.aac,.ogg,.oga,.opus,.webm,.mp4,.mpeg,.mpga,.flac,audio/*"
                    onChange={(event) => {
                      void uploadFiles(event.target.files);
                      event.target.value = "";
                    }}
                    disabled={
                      sending
                      || restoring
                      || uploadingAttachment
                      || attachments.length >= 5
                    }
                  />
                  <div className="justiniano-media-actions">
                    <button
                      type="button"
                      className="justiniano-attach-button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={
                        sending
                        || restoring
                        || uploadingAttachment
                        || recordingAudio
                        || attachments.length >= 5
                      }
                      aria-label="Adjuntar archivos"
                      title="Adjuntar PDF, documento, imagen o audio (máx. 10 MB)"
                    >
                      {uploadingAttachment ? (
                        <LoaderCircle className="is-spinning" size={18} />
                      ) : (
                        <Paperclip size={18} />
                      )}
                    </button>
                    <button
                      type="button"
                      className={`justiniano-audio-button${recordingAudio ? " is-recording" : ""}`}
                      onClick={recordingAudio
                        ? stopAudioRecording
                        : () => { void startAudioRecording(); }}
                      disabled={
                        sending
                        || restoring
                        || uploadingAttachment
                        || (!recordingAudio && attachments.length >= 5)
                      }
                      aria-label={recordingAudio
                        ? "Detener grabación"
                        : "Grabar mensaje de audio"}
                      aria-pressed={recordingAudio}
                      title={recordingAudio
                        ? "Detener y adjuntar audio"
                        : "Grabar mensaje de audio"}
                    >
                      {recordingAudio ? <Square size={16} /> : <Mic size={18} />}
                    </button>
                  </div>
                </>
              )}
              {responseModes.length > 0 && (
                <div className="justiniano-composer-options">
                  {responseModes.length > 0 && (
                    <label className="justiniano-response-mode">
                      <select
                        value={responseMode || ""}
                        onChange={(event) => setResponseMode(
                          persistResponseModePreference(event.target.value, {
                            appId,
                            responseModes,
                            defaultResponseMode,
                          }),
                        )}
                        aria-label="Modo de respuesta"
                        title="Ajusta la forma de explicar la respuesta"
                      >
                        {responseModes.map(({ value, label }) => (
                          <option key={value} value={value}>Modo: {label}</option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
              )}
              <textarea
                ref={inputRef}
                rows={1}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    send();
                  }
                }}
                placeholder={recordingAudio
                  ? "Grabando audio…"
                  : `Escribile a ${assistantName}…`}
                aria-label={`Mensaje para ${assistantName}`}
              />
              <button
                type="submit"
                disabled={
                  (!draft.trim() && !attachments.some(
                    (attachment) => attachment.kind === "audio",
                  ))
                  || sending
                  || restoring
                  || uploadingAttachment
                  || recordingAudio
                }
                aria-label="Enviar mensaje"
              >
                {sending ? (
                  <LoaderCircle className="is-spinning" size={18} />
                ) : (
                  <Send size={18} />
                )}
              </button>
            </div>
            {allowAttachments && attachmentError && (
              <span className="justiniano-attachment-error" role="alert">
                {attachmentError}
              </span>
            )}
            <small>
              {assistantName} puede equivocarse. Verificá la información jurídica
              importante.
            </small>
            {poweredBy?.label && (
              <small className="justiniano-powered-by">
                {poweredBy.url ? (
                  <a href={poweredBy.url} target="_blank" rel="noreferrer">
                    {poweredBy.label}
                  </a>
                ) : poweredBy.label}
              </small>
            )}
          </form>
          )}

          {!historyOpen && activeView === "drafting" && (
            <DraftingErrorBoundary>
              <Suspense fallback={(
                <div className="justiniano-drafting-loading">
                  <LoaderCircle className="is-spinning" size={20} />
                  Preparando el editor…
                </div>
              )}>
                <DraftCanvas
                  appId={appId}
                  assistantName={assistantName}
                  handoff={draftHandoff}
                  loadProfile={client.getProfile}
                  signatureSettingsHref={resolvedSignatureSettingsHref}
                  onHandoffHandled={(handoffId) => {
                    setDraftHandoff((current) => (
                      current?.handoffId === handoffId ? null : current
                    ));
                  }}
                  onRequestAiEdit={editDraftDocument}
                  onRouteToChat={(prompt) => {
                    const question = String(prompt || "").trim();
                    setActiveView("chat");
                    if (question) void send(question, { skipDraftTool: true });
                  }}
                />
              </Suspense>
            </DraftingErrorBoundary>
          )}

          {assignmentOpen && conversationAssignment && (
            <ConversationAssignmentDialog
              assignment={activeAssignment}
              busy={assignmentBusy}
              config={conversationAssignment}
              error={assignmentError}
              loading={assignmentLoading}
              onAssign={(target) => { void assignActiveConversation(target); }}
              onClose={() => setAssignmentOpen(false)}
              onRemove={() => { void removeActiveConversationAssignment(); }}
              targets={assignmentTargets}
            />
          )}
        </section>
      )}

      {!pageVariant && (
        <>
          {!visible && proactiveTip && (
            <aside className="justiniano-proactive-tip" role="status">
              <button
                className="justiniano-proactive-tip-copy"
                type="button"
                onClick={() => {
                  dismissProactiveTip();
                  setOpen(true);
                }}
              >
                <AssistantMark size={16} />
                <span>{proactiveTip.text}</span>
              </button>
              <button
                className="justiniano-proactive-tip-close"
                type="button"
                onClick={dismissProactiveTip}
                aria-label="Cerrar sugerencia de Justiniano"
              >
                <X size={14} />
              </button>
            </aside>
          )}
          <button
            ref={triggerRef}
            type="button"
            className="justiniano-trigger"
            aria-expanded={open}
            aria-controls={`justiniano-panel-${appId}`}
            aria-label={open ? `Cerrar ${assistantName}` : `Abrir ${assistantName}`}
            onClick={() => {
              if (!open) dismissProactiveTip();
              setOpen((current) => !current);
            }}
          >
            {open ? <X size={21} /> : <MessageCircle size={21} />}
            {!open && <span>{assistantName}</span>}
          </button>
        </>
      )}
    </div>
  );
}
